/**
 * Base Service Class
 * Provides common functionality for all API services
 *
 * Features:
 * - Automatic auth token injection
 * - Team-scoped API calls
 * - Error handling
 * - Type-safe responses
 */

import { authService } from './auth/authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

export class BaseService {
  protected baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get authentication headers
   */
  protected async getHeaders(additionalHeaders?: HeadersInit): Promise<HeadersInit> {
    const token = await this.getAuthToken();

    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...additionalHeaders,
    };
  }

  /**
   * Get auth token from auth service (works for both local and Cognito modes)
   */
  protected async getAuthToken(): Promise<string | null> {
    return authService.getToken();
  }

  /**
   * Handle API response with proper error handling
   */
  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.statusText || 'Request failed',
        code: `HTTP_${response.status}`
      }));

      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Make a GET request
   */
  protected async get<T>(path: string, headers?: HeadersInit): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    console.log('[BaseService] GET', url);
    const response = await fetch(url, {
      method: 'GET',
      headers: await this.getHeaders(headers),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Make a POST request
   */
  protected async post<T>(path: string, body?: any, headers?: HeadersInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: await this.getHeaders(headers),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Make a PUT request
   */
  protected async put<T>(path: string, body?: any, headers?: HeadersInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: await this.getHeaders(headers),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Make a PATCH request
   */
  protected async patch<T>(path: string, body?: any, headers?: HeadersInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PATCH',
      headers: await this.getHeaders(headers),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Make a DELETE request
   */
  protected async delete<T>(path: string, headers?: HeadersInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: await this.getHeaders(headers),
    });

    return this.handleResponse<T>(response);
  }
}

/**
 * Team-scoped service base class
 * Automatically prefixes all requests with /teams/:teamId
 */
export class TeamScopedService extends BaseService {
  /**
   * Build team-scoped path
   */
  protected getTeamPath(teamId: string, path: string): string {
    return `/teams/${teamId}${path}`;
  }

  /**
   * Make a GET request with team scope
   */
  protected async getTeam<T>(teamId: string, path: string, headers?: HeadersInit): Promise<T> {
    return this.get<T>(this.getTeamPath(teamId, path), headers);
  }

  /**
   * Make a POST request with team scope
   */
  protected async postTeam<T>(teamId: string, path: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.post<T>(this.getTeamPath(teamId, path), body, headers);
  }

  /**
   * Make a PUT request with team scope
   */
  protected async putTeam<T>(teamId: string, path: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.put<T>(this.getTeamPath(teamId, path), body, headers);
  }

  /**
   * Make a PATCH request with team scope
   */
  protected async patchTeam<T>(teamId: string, path: string, body?: any, headers?: HeadersInit): Promise<T> {
    return this.patch<T>(this.getTeamPath(teamId, path), body, headers);
  }

  /**
   * Make a DELETE request with team scope
   */
  protected async deleteTeam<T>(teamId: string, path: string, headers?: HeadersInit): Promise<T> {
    return this.delete<T>(this.getTeamPath(teamId, path), headers);
  }
}
